import React, { useCallback, useMemo } from 'react'
import {
  MAX_TRACK_GAIN_DB,
  MIN_TRACK_GAIN_DB,
  trackGainDbToPercent,
} from '@/shared/track-gain.ts'

export const formatGainDb = (gainDb: number): string => {
  const rounded = Math.round(gainDb * 10) / 10
  if (rounded === 0) {
    return '0 dB'
  }
  const sign = rounded > 0 ? '+' : ''
  return `${sign}${rounded} dB`
}

interface GainSliderProps {
  value: number
  onChange: (gainDb: number) => void
  className?: string
}

const GainSlider: React.FC<GainSliderProps> = ({
  value,
  onChange,
  className = '',
}) => {
  const sliderPercent = useMemo(() => trackGainDbToPercent(value), [value])
  const gainLabel = useMemo(() => formatGainDb(value), [value])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(Number(e.target.value))
    },
    [onChange]
  )

  return (
    <div className={`group ${className}`}>
      <div className="mb-0.5 flex justify-end">
        <span className="text-[10px] font-medium tabular-nums text-gray-300">
          {gainLabel}
        </span>
      </div>

      <div className="relative flex h-4 w-full items-center">
        <div className="absolute h-2 w-full border border-black bg-zinc-800" />

        <div
          className="absolute h-2 border border-r-0 border-black bg-green-500"
          style={{ width: `${sliderPercent}%` }}
        />

        <div
          className="absolute h-4 w-2 rounded-sm bg-white opacity-0 shadow-md transition-opacity group-hover:opacity-100"
          style={{
            left: `${sliderPercent}%`,
            transform: 'translateX(-50%)',
          }}
        />

        <input
          type="range"
          min={MIN_TRACK_GAIN_DB}
          max={MAX_TRACK_GAIN_DB}
          step={0.1}
          value={value}
          onChange={handleChange}
          aria-label="Track gain"
          className="absolute z-10 h-4 w-full cursor-pointer opacity-0"
        />
      </div>
    </div>
  )
}

export default React.memo(GainSlider)
